import React, { useState, useEffect, useMemo } from 'react';
import { useForm, Controller, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Add, Visibility, VisibilityOff } from '@mui/icons-material';
import { PermissionButton } from '@/components/common/PermissionButton/PermissionButton';
import useAuth0Redirect from '../../hooks/useAuth0Redirect';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import PhoneInput from '@/components/common/PhoneInput/PhoneInput';

import { useGetRolesDropdownQuery  } from '../../store/api/roleApi';
import { useGetAppAccessUsersDropdownQuery, useInviteUserMutation, useGetAuth0MeQuery, UserInviteInput, UserCreationMode } from '../../store/api/userApi';
import { useUpdateWorkflowTransactionMutation } from '@/store/api/workflowTransactionApi';
import { useGetPhoneCountryCodesQuery } from '../../store/api/phoneCodeApi';
import { getUsersRoles, isSuperAdmin } from '../../utils/getUsersRoles';
import AddRoleForm from "../../components/users/AddRoleForm";
import { useDebounce } from '../../hooks/useDebounce';
import { downloadCredentialsExcel } from '@/utils/downloadCredentialsExcel';

import {
  Container,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import { useToast } from '../common/Toast/ToastProvider';
import AutoCompleteSelect from '@/components/common/AutoCompleteSelect';
import { parseApiError } from '@/utils/apiFieldErrors';

// Base validation shape shared regardless of the org's user_creation_mode —
// the `password` field is added dynamically in buildValidationSchema() below,
// since whether it's required/optional/absent depends on that org setting.
const baseValidationShape = {
  first_name: yup
    .string()
    .required('First Name is required')
    .matches(/^[A-Za-z\s'-]+$/, 'First Name may contain letters, spaces, hyphens, and apostrophes only'),
  last_name: yup
    .string()
    .required('Last Name is required')
    .matches(/^[A-Za-z\s'-]+$/, 'Last Name may contain letters, spaces, hyphens, and apostrophes'),
  email: yup
    .string()
    .email('Invalid email format')
    .required('Email is required'),
  employee_id: yup
    .string()
    .required('Employee ID is required')
    .matches(/^[A-Za-z0-9_-]+$/, 'Employee ID should not contain special characters'),
  role: yup.string().required('Role is required'),
  phone_number: yup
    .string()
    .required('Contact Number is required')
    .test('valid-phone', 'Invalid phone number format', (value) => {
      if (!value || !value.trim()) return true; // required() handles the empty case
      const parts = value.trim().split(' ');
      return parts.length === 2 && parts[0].startsWith('+') && /^\d+$/.test(parts[1]);
    }),
  reporting_manager: yup.string().default(''),
};

// `direct`: password required. `invite`: password field isn't rendered at all
// — schema just needs to accept its absence. Same when application access is
// disabled: the password field isn't rendered regardless of mode, so it must
// not be required either.
// The field stays a uniformly-optional yup.string() regardless of mode — the
// "required in direct mode" rule is enforced via .test() rather than a
// branching .required()/.notRequired() schema, so buildValidationSchema()
// always returns the same inferred shape regardless of `userCreationMode`.
const buildValidationSchema = (userCreationMode: UserCreationMode, hasAppAccess: boolean) =>
  yup.object().shape({
    ...baseValidationShape,
    password: yup
      .string()
      .notRequired()
      .test('password-required', 'Password is required', (value) => {
        if (userCreationMode !== 'direct' || !hasAppAccess) return true;
        return !!value;
      })
      .test('password-min-length', 'Password must be at least 8 characters', (value) => {
        if (!value) return true; // absence is handled by the test above
        return value.length >= 8;
      }),
  });

interface UserFormInputs {
  first_name: string;
  last_name: string;
  email: string;
  employee_id: string;
  role: string;
  phone_number: string;
  reporting_manager?: string;
  password?: string;
}

// Initial form values
const initialValues = {
  first_name: '',
  last_name: '',
  email: '',
  employee_id: '',
  role: '',
  phone_number: '',
  reporting_manager: '',
  password: '',
};

interface InviteUserFormProps {
  isWorkflowEdit?: boolean;
  modifiable?: string[];
  initialData?: UserInviteInput;
  workflowTransaction?: string;
  setIsEditWorkflow?: React.Dispatch<React.SetStateAction<boolean>>;
  approvalTaskRefetch?: () => void;
}

const InviteUserForm: React.FC<InviteUserFormProps> = ({
  isWorkflowEdit = false,
  initialData,
  modifiable,
  workflowTransaction,
  setIsEditWorkflow,
  approvalTaskRefetch,
}) => {
  useAuth0Redirect();
  const { user } = useAuth0();
  const navigate = useNavigate();

  const [openAddRole, setOpenAddRole] = useState(false);
  const [isSuperAdminUser, setIsSuperAdminUser] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // "Enable Application Access" — creation-time-only toggle. When disabled,
  // no login profile is created for this user at all (no IdP account, no
  // invite, no password); the user exists purely for asset-assignment
  // tracking. Defaults to enabled (matches the model's has_app_access=True
  // default).
  const [hasAppAccess, setHasAppAccess] = useState(true);
  const { showToast } = useToast();

  const [inviteUser, { isLoading }] = useInviteUserMutation();
  const [updateTransaction] = useUpdateWorkflowTransactionMutation();

  // Drives whether the password field is shown/required — see
  // company_details.feature_flags.user_creation_mode on the auth0/me response.
  // Defaults to 'invite' (current behavior) while the query is loading or for
  // orgs that haven't set the flag yet. Workflow-approval edits are always for
  // previously-invited users, so the password field never applies there.
  const { data: auth0MeData } = useGetAuth0MeQuery();
  const userCreationMode: UserCreationMode =
    auth0MeData?.user?.company_details?.feature_flags?.user_creation_mode || 'invite';
  // Disabling "Enable Application Access" hides the password field
  // regardless of the org's user_creation_mode — an access-disabled user
  // never gets a password, invite, or IdP account.
  const showPasswordField = !isWorkflowEdit && userCreationMode !== 'invite' && hasAppAccess;

  // Dropdown state management
  const [roleState, setRoleState] = useState({
    search: '',
    page: 1,
    data: [] as any[],
    hasMore: true,
  });
  const [userState, setUserState] = useState({
    search: '',
    page: 1,
    data: [] as any[],
    hasMore: true,
  });
  const debouncedRoleSearch = useDebounce(roleState.search, 300);
  const debouncedUserSearch = useDebounce(userState.search, 300);

  const { data: phoneCountryCodes = [], isLoading: isPhoneCodesLoading, error: phoneCodesError } = useGetPhoneCountryCodesQuery();

  const {
    data: rolesData,
    isLoading: isRolesLoading,
    refetch: refetchRoles,
    isFetching: isRolesFetching
  } = useGetRolesDropdownQuery({ 
    page: roleState.page, 
    dropdown: true, 
    limit: 25, 
    search: debouncedRoleSearch || undefined 
  });
  // Reporting Manager must be an actual AMS user (someone who can log in and
  // review/approve), so this is the "valid-login-only" list, not the
  // complete list — a profile-only (has_app_access=False) user can't be
  // someone's reporting manager.
  const {
    data: usersData,
    isLoading: isUsersLoading,
    isFetching: isUsersFetching
  } = useGetAppAccessUsersDropdownQuery({
    page: userState.page,
    dropdown: true,
    limit: 25,
    search: debouncedUserSearch || undefined
  });
  const roleOptions = useMemo(() => {
    return roleState.data.map((role: any) => ({
      label: `${role.name}${role.description ? ` (${role.description})` : ''}`,
      value: role.id,
    }));
  }, [roleState.data]);

  const userOptions = useMemo(() => {
    return userState.data.map((user: any) => ({
      label: user.name || 'Unknown User',
      value: user.id,
    }));
  }, [userState.data]);

  const prevRoleSearchRef = React.useRef('');

  useEffect(() => {
    if (rolesData?.results) {
      setRoleState((prev) => {
        const isNewSearch =
          prevRoleSearchRef.current !== prev.search;

        prevRoleSearchRef.current = prev.search;

        return {
          ...prev,
          data: isNewSearch
            ? rolesData.results   // ✅ replace data on search
            : prev.page === 1
            ? rolesData.results
            : [...prev.data, ...rolesData.results],
          hasMore: !!rolesData.next,
        };
      });
    }
  }, [rolesData]);

  useEffect(() => {
    if (!usersData?.results) return;

    setUserState((prev) => ({
      ...prev,
      data:
        prev.page === 1
          ? usersData.results
          : [...prev.data, ...usersData.results],
      hasMore: !!usersData.next,
    }));
  }, [usersData]);

  const handleRoleSearch = (value: string) => {
    setRoleState({
      search: value,
      page: 1,
      data: [],
      hasMore: true,
    });
  };
  const handleUserSearch = (value: string) => {
    setUserState({
      search: value,
      page: 1,
      data: [],
      hasMore: true,
    });
  };
  const handleRoleScrollEnd = () => {
    if (roleState.hasMore && !isRolesLoading && !isRolesFetching) {
      setRoleState(prev => ({
        ...prev,
        page: prev.page + 1
      }));
    }
  };
  
  const handleUserScrollEnd = () => {
    if (userState.hasMore && !isUsersLoading && !isUsersFetching) {
      setUserState(prev => ({
        ...prev,
        page: prev.page + 1
      }));
    }
  };
  
  useEffect(() => {
    const checkUserRole = async () => {
      if (user) {
        try {
          const userRoles = await getUsersRoles(user);
          setIsSuperAdminUser(isSuperAdmin(userRoles));
        } catch (error) {
          showToast('Failed to check user roles', 'error');
        }
      }
    };

    checkUserRole();
  }, [user]);

  const validationSchema = useMemo(
    () => buildValidationSchema(userCreationMode, hasAppAccess),
    [userCreationMode, hasAppAccess]
  );

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<UserFormInputs>({
    // yup infers `password` (a .notRequired() field with no .default()) as an
    // optional-vs-required key differently than the hand-written
    // UserFormInputs interface below, which is a known yupResolver/yup
    // generic-inference friction point — the cast is safe since the schema's
    // actual runtime validation still matches UserFormInputs' fields exactly.
    resolver: yupResolver(validationSchema) as Resolver<UserFormInputs>,
    defaultValues: initialValues,
  });

  useEffect(() => {
    if (isWorkflowEdit && initialData) {
      reset({
        ...initialData,
      });
      setHasAppAccess(initialData.has_app_access ?? true);
    }
  }, [isWorkflowEdit, initialData, reset]);
  

  const isFieldEnabled = (fieldName: string) => {
    return modifiable && modifiable.includes(fieldName);
  };

  const onInvalid = () => {
    showToast('Please fill in all required fields correctly.', 'error');
  };

  const onSubmit = async (values: UserFormInputs) => {
    try {
      const userData: UserInviteInput = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        employee_id: values.employee_id,
        role: values.role,
        phone_number: values.phone_number.trim(),
      };
      if (values.reporting_manager) {
        userData.reporting_manager = values.reporting_manager;
      }
      userData.has_app_access = hasAppAccess;
      if (showPasswordField && values.password) {
        userData.password = values.password;
      }

      if(isWorkflowEdit && initialData && workflowTransaction) {
        await updateTransaction({
          id: workflowTransaction,
          formData: userData,
        }).unwrap();
        showToast('Form updated successfully', 'success');
        if(setIsEditWorkflow) {
          setIsEditWorkflow(false);
        }
        if(approvalTaskRefetch){
          approvalTaskRefetch();
        }
        return;
      }

      await inviteUser(userData).unwrap();

      const createdDirectly = !!userData.password;
      showToast(
        createdDirectly
          ? 'User created successfully'
          : isSuperAdminUser ? 'Owner invited successfully' : 'User invited successfully',
        'success'
      );

      // Direct-creation mode: the admin typed this password and can never
      // view it again afterwards, so hand it over once as a spreadsheet —
      // same handoff mechanism used after an admin-triggered password reset.
      if (createdDirectly && userData.password) {
        downloadCredentialsExcel({
          organization: auth0MeData?.user?.company_details?.name || '',
          email: userData.email,
          password: userData.password,
        });
      }

      reset(initialValues);
      navigate('/users', { replace: true });
    } catch (err) {
      if (typeof err === 'object' && err !== null && (err as { status?: number }).status === 429) {
        showToast('Too many invitation requests. Please try again later.', 'error');
        return;
      }

      const { fieldErrors, message } = parseApiError(err);
      const formFields: (keyof UserFormInputs)[] = [
        'first_name',
        'last_name',
        'email',
        'employee_id',
        'role',
        'phone_number',
        'reporting_manager',
      ];
      let mappedAny = false;
      formFields.forEach((field) => {
        if (fieldErrors[field]) {
          setError(field, { type: 'server', message: fieldErrors[field] });
          mappedAny = true;
        }
      });

      if (!mappedAny) {
        showToast(message || 'Failed to invite user', 'error');
      }
    }
  };

  return (
    <Container maxWidth="lg">
      <Box className="formWrapper">
        <Typography
          variant="h5"
          component="h2"
          className="formTitle"
          sx={{ mb: 3 }}
        >
          {userCreationMode === 'direct'
            ? (isSuperAdminUser ? 'Create New Owner' : 'Create New User')
            : (isSuperAdminUser ? 'Invite New Owner' : 'Invite New User')}
        </Typography>

        <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {/* First Name */}
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '240px' }}>
              <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                First Name *
              </Typography>
              <Controller
                name="first_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="First Name"
                    error={!!errors.first_name}
                    helperText={errors.first_name?.message}
                    variant="outlined"
                    sx={{ mb: 2 }}
                    disabled={isWorkflowEdit && !isFieldEnabled(field.name)}
                  />
                )}
              />
            </Box>

            {/* Last Name */}
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '240px' }}>
              <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                Last Name *
              </Typography>
              <Controller
                name="last_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="Last Name"
                    error={!!errors.last_name}
                    helperText={errors.last_name?.message}
                    variant="outlined"
                    sx={{ mb: 2 }}
                    disabled={isWorkflowEdit && !isFieldEnabled(field.name)}
                  />
                )}
              />
            </Box>

            {/* Email */}
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '240px' }}>
              <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                Email Address *
              </Typography>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="Email Address"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    variant="outlined"
                    sx={{ mb: 2 }}
                    disabled={isWorkflowEdit && !isFieldEnabled(field.name)}
                  />
                )}
              />
            </Box>

            {/* Employee ID */}
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '240px' }}>
              <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                Employee Id *
              </Typography>
              <Controller
                name="employee_id"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="Employee ID"
                    error={!!errors.employee_id}
                    helperText={errors.employee_id?.message}
                    variant="outlined"
                    sx={{ mb: 2 }}
                    disabled={isWorkflowEdit && !isFieldEnabled(field.name)}
                  />
                )}
              />
            </Box>

            {/* Role */}
            <Box sx={{
              flex: '1 1 calc(50% - 8px)', 
              minWidth: '240px', 
              display: 'flex', 
              alignItems: 'flex-start',
              flexDirection: 'column',
              gap: 1,
              }}
            >
              <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                Role *
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>  
                <FormControl fullWidth error={!!errors.role} disabled={isRolesLoading}>
                  <Controller
                    name="role"
                    control={control}
                    render={({ field }) => (
                      <>
                        <AutoCompleteSelect
                          fullwidth
                          options={roleOptions}
                          value={field.value}
                          loading={isRolesLoading || isRolesFetching}
                          placeholder="Select Role"
                          searchable
                          error={!!errors.role}
                          searchValue={roleState.search}
                          onSearchChange={handleRoleSearch}
                          onChange={(value) => field.onChange(value)}
                          onScrollEnd={handleRoleScrollEnd}
                        />
                        {errors.role && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ ml: 1, mt: 0.5 }}
                          >
                            {errors.role.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </FormControl>
                <Tooltip title="Add Role" arrow>
                  <span>
                    <PermissionButton
                      module="role"
                      permissionAction="add"
                      variant="contained"
                      onClick={() => setOpenAddRole(true)}
                      disabled={isRolesLoading}
                      sx={{
                        mb: 1,
                        minWidth: '40px',
                        height: '40px',
                        padding: 0,
                        backgroundColor: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                      }}
                    >
                      <Add />
                    </PermissionButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {/* Contact Number */}
            <Box
              sx={{
                flex: '1 1 calc(50% - 8px)',
                minWidth: '240px',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                Contact Number *
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                <PhoneInput
                  control={control}
                  name="phone_number"
                  countryCodes={phoneCountryCodes.map((country) => ({
                    code: country.dial_code,
                    flag: country.region,
                    name: country.name,
                  }))}
                  placeholder="Contact Number"
                  error={errors.phone_number}
                  disabled={isWorkflowEdit && !isFieldEnabled('phone_number')}
                  isLoading={isPhoneCodesLoading}
                  hasError={!!phoneCodesError}
                />
              </Box>
            </Box>

            {/* Reporting Manager */}
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '240px' }}>
              <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                Reporting Manager
              </Typography>
              <FormControl fullWidth error={!!errors.reporting_manager} disabled={isUsersLoading} >
                <Controller
                  name="reporting_manager"
                  control={control}
                  render={({ field }) => (
                    <AutoCompleteSelect
                      fullwidth
                      options={userOptions}
                      value={field.value || ''}
                      loading={isUsersLoading || isUsersFetching}
                      placeholder="Select Reporting Manager"
                      searchable
                      searchValue={userState.search}
                      onSearchChange={handleUserSearch}
                      onChange={(value) => field.onChange(value)}
                      onScrollEnd={handleUserScrollEnd}
                    />
                  )}
                />
              </FormControl>
            </Box>

            {/* Enable Application Access */}
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '240px' }}>
              <Typography variant="caption" sx={{ mb: 1, fontWeight: 500, display: 'block' }}>
                Enable Application Access
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={hasAppAccess}
                    onChange={(e) => setHasAppAccess(e.target.checked)}
                    disabled={isWorkflowEdit && !isFieldEnabled('has_app_access')}
                  />
                }
                label={hasAppAccess ? 'Enabled — user can log in to AMS' : 'Disabled — profile only, no AMS login'}
              />
            </Box>

            {/* Password — shown only for orgs whose user_creation_mode is 'direct', and only when application access is enabled */}
            {showPasswordField && (
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '240px' }}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Password *
                </Typography>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Set a password for this user"
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      variant="outlined"
                      autoComplete="new-password"
                      sx={{ mb: 2 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                              onClick={() => setShowPassword((v) => !v)}
                              edge="end"
                              size="small"
                              tabIndex={-1}
                            >
                              {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            )}

            {/* Submit Button */}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                mt: 2,
              }}
            >
              {!isWorkflowEdit && 
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => reset()}
                  disabled={isLoading}
                >
                  Reset
                </Button>
              }
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading}
                className="button button__solid--primary"
              >
                {isLoading
                  ? <CircularProgress size={24} />
                  : isWorkflowEdit
                    ? 'Save'
                    : userCreationMode === 'direct' ? 'Create User' : 'Invite User'}
              </Button>
            </Box>
          </Box>
        </form>

        {/* ---------------- ADD ROLE POPUP ---------------- */}
        <AddRoleForm
          open={openAddRole}
          onClose={() => setOpenAddRole(false)}
          onSuccess={refetchRoles}
        />
      </Box>
    </Container>
  );
};

export default InviteUserForm;